import masonry from 'alpinejs-masonry'

document.addEventListener('alpine:init', () => {
    Alpine.plugin(masonry);
});

/*
 * Open layers (dialogs, drawers, dropdowns), newest last. They can open inside each other, and
 * Escape, a click outside and the browser's Back button each close only the top one, going back
 * a level at a time. Opening a layer adds a history entry (without Livewire's state, so Livewire
 * ignores it); closing it any other way takes that entry back off.
 */
let layerIds = 0;

export const layers = {
    stack: [],
    skipPop: false,
    next: null,     // a page to go to once the layers' history entries are gone
    locks: 0,
    closing: false, // a layer closed during the current click (close())

    top() {
        return this.stack[this.stack.length - 1];
    },

    push(layer) {
        this.stack.push(layer);
        history.pushState({layer: this.stack.length}, '');
    },

    remove(layer) {
        const index = this.stack.findIndex((open) => open.layerId === layer.layerId);
        if (index === -1) return;

        this.stack.splice(index, 1);
        queueMicrotask(() => this.syncHistory());
    },

    // Closed by Escape, a click or a button: drop the history entries those layers added, in one
    // step even when several closed together
    syncHistory() {
        const extra = (history.state?.layer ?? 0) - this.stack.length;

        if (extra > 0 && !this.skipPop) {
            this.skipPop = true;
            history.go(-extra);
        }
    },

    // Drawers lock page scrolling; it stays locked until the last of them closes
    lock(on) {
        this.locks = Math.max(0, this.locks + (on ? 1 : -1));
        document.body.style.overflow = this.locks ? 'hidden' : '';
    },
};

// Back closes the top layer instead of leaving the page (runs before Livewire's own handler)
window.addEventListener('popstate', (event) => {
    if (layers.skipPop) {
        layers.skipPop = false;
        event.stopImmediatePropagation();
        // The layers' entries are gone: now go where the page was going
        if (layers.next) {
            const url = layers.next;
            layers.next = null;
            return Livewire.navigate(url);
        }
        // More layers may have closed while that step was on its way
        queueMicrotask(() => layers.syncHistory());
        return;
    }

    const depth = event.state?.layer;

    if (layers.stack.length > (depth ?? 0)) {
        event.stopImmediatePropagation();
        while (layers.stack.length > (depth ?? 0)) {
            // One that asks its content first: its history entry goes back on, and the content decides
            if (layers.top().leave) {
                history.pushState({layer: layers.stack.length}, '');
                return layers.top().dismiss();
            }
            layers.top().close();
        }
    } else if (depth && !layers.stack.length) {
        // An entry left by a layer the page has since navigated away from: step past it
        event.stopImmediatePropagation();
        history.back();
    }
}, {capture: true});

// Escape closes the top layer only. preventDefault stops the browser closing a whole group of
// dialogs at once; Android's back gesture still reaches dialogs through their cancel event
document.addEventListener('keydown', (event) => {
    const top = layers.top();
    if (event.key !== 'Escape' || event.defaultPrevented || !top) return;

    event.preventDefault();
    top.dismiss(top.$refs.trigger);
});

// Leaving the page from an open layer (a menu's link, a dialog's form that redirects): take the layers'
// history entries back first, then go, so Back returns to this page rather than to a stale menu entry
document.addEventListener('livewire:navigate', (event) => {
    const extra = history.state?.layer ?? 0;
    if (event.detail.history || extra < 1) return;

    event.preventDefault();
    layers.skipPop = true;
    layers.next = event.detail.url.href;
    [...layers.stack].reverse().forEach((layer) => layer.close());
    history.go(-extra);
});

// Back (or Forward) to checkout loads it afresh, never from Livewire's saved copy: the payment providers' frames in a
// saved copy are dead (a PayPal dialog came back open and broken), and the cart may have changed or been paid since
document.addEventListener('livewire:navigate', (event) => {
    if (!event.detail.history || !/^\/checkout(\/|$)/.test(event.detail.url.pathname)) return;

    event.preventDefault();
    window.location.replace(event.detail.url.href);
});

// A page change (wire:navigate) takes every layer with it
document.addEventListener('livewire:navigating', () => {
    layers.stack.forEach((layer) => (layer.show = false));
    layers.stack = [];
    layers.locks = 1;
    layers.lock(false);
});

// Shared open/close behaviour for dropdowns, drawers and dialogs. Each can be driven from anywhere
// with a window event: $dispatch('dialog-search') toggles, $dispatch('dialog-search', 'open') opens.
// native: a <dialog> (the browser can also close it, e.g. Android's back gesture)
export const openable = (hooks = {}, native = false) => ({
    show: false,
    native,
    // Alpine hands out different proxies of the same component, so layers compare by id
    layerId: ++layerIds,
    // An event its content answers before it closes (x-dialog leave: the photo editor asks first when there are
    // edits): ×, Escape and Back send it, and a click outside does nothing
    leave: null,

    /** ×, Escape, Back: closed, or its content asked first */
    dismiss(focusAfter) {
        this.leave ? window.dispatchEvent(new CustomEvent(this.leave)) : this.close(focusAfter);
    },

    open() {
        if (this.show) return;

        this.show = true;
        layers.push(this);
        this.watch();
        hooks.opened?.call(this);
    },

    close(focusAfter) {
        if (!this.show) return;

        this.show = false;
        this.watcher?.destroy();
        this.watcher = null;
        hooks.closed?.call(this);
        layers.remove(this);

        // A click that closed a layer (a button in it, like a dialog's Got It) is done: the same click then reaching
        // the layer beneath isn't an outside click for that one
        layers.closing = true;
        setTimeout(() => layers.closing = false);

        focusAfter?.focus();
    },

    /**
     * Android's back gesture goes to the newest close watcher before the page's history. A <dialog> is one by
     * itself; a dropdown or drawer gets its own, so Back closes it rather than asking the dialog beneath (which
     * Chrome only lets a page refuse once per tap, so a second Back would close the dialog instead).
     */
    watch() {
        if (this.native || !('CloseWatcher' in window)) return;

        try {
            this.watcher = new CloseWatcher();
            this.watcher.onclose = () => this.close(this.$refs.trigger);
        } catch {
            this.watcher = null;
        }
    },

    toggle() {
        this.show ? this.close(this.$refs.trigger) : this.open();
    },

    run(method) {
        // From PHP, $this->dispatch('dialog-x', 'close') arrives as ['close']: without unwrapping it, every
        // close from the server toggled instead (and opened a closed dialog)
        method = Array.isArray(method) ? method[0] : method;

        if (method === "open") this.open();
        else if (method === "close") this.close();
        else this.toggle();
    },

    /**
     * The browser asked this <dialog> to close (Android's back gesture, where close watchers aren't supported):
     * the top layer closes, which may be a dropdown or drawer inside it rather than the dialog.
     */
    cancel(focusAfter) {
        const top = layers.top();

        if (top && top.layerId !== this.layerId) top.close(top.$refs?.trigger);
        else this.dismiss(focusAfter);
    },

    /** Only the newest open layer answers Escape, outside clicks and Back. */
    get isTop() {
        return layers.top()?.layerId === this.layerId;
    },

    /**
     * An outside click or focus: closes this layer when it's on top. One click closes one layer
     * (the layer beneath becomes top during the same click), and toasts don't count.
     */
    outside(event, focusAfter) {
        if (event.layerClosed || layers.closing || this.leave || !this.isTop || event.target.closest?.('#toaster')) return;

        event.layerClosed = true;
        this.close(focusAfter);
    },
});

/**
 * Keeps an open menu on the screen, 12px in: slid back when it would run off a side (zoomed in, a
 * narrow window), and stopping above the bottom (and the phone's tab bar), scrolling when longer.
 * Measured each time it opens, from where its alignment puts it.
 */
window.keepOnScreen = (menu, gap = 12) => {
    Object.assign(menu.style, { left: '', right: '', translate: '', maxHeight: '', overflowY: '', top: '', bottom: '', marginTop: '', marginBottom: '' });

    // Layout positions, not the box on screen: the opening animation scales the menu down at first
    const parent = menu.offsetParent?.getBoundingClientRect() ?? { left: 0, top: 0 };
    const left = parent.left + menu.offsetLeft;
    // Clear of any sidebar too (resources/js/bars.js; 0 while there are none)
    const side = (name) => parseFloat(getComputedStyle(document.documentElement).getPropertyValue(name)) || 0;
    const shift = Math.max(gap + side('--left-offset') - left, Math.min(0, window.innerWidth - gap - side('--right-offset') - (left + menu.offsetWidth)));

    if (shift) {
        Object.assign(menu.style, { left: `${menu.offsetLeft + shift}px`, right: 'auto', translate: 'none' });
    }

    // The room below it, above the tab bar (resources/js/bars.js); never squeezed below a few rows
    const room = window.innerHeight - side('--bottom-offset') - gap - (parent.top + menu.offsetTop);

    // One that may open upwards (data-flip, x-dropdown flip): above its trigger when there's more room there
    const above = parent.top - gap - side('--top-offset');
    if (menu.offsetHeight > room && menu.hasAttribute('data-flip') && above > room) {
        Object.assign(menu.style, { top: 'auto', bottom: '100%', marginTop: '0', marginBottom: `${gap}px` });
        if (menu.offsetHeight > above) Object.assign(menu.style, { maxHeight: `${above}px`, overflowY: 'auto' });
        return;
    }

    if (menu.offsetHeight > room) {
        Object.assign(menu.style, { maxHeight: `${Math.max(room, 120)}px`, overflowY: 'auto' });
    }
};

// A gallery's shown item (x-media.gallery), remembered by gallery across the page's own refreshes (an item added,
// replaced or deleted rebuilds the gallery): one just added (owner: an upload shows it), else the same one if it's still
// there, else the one before it, else after, else the first
const galleries = {};

Alpine.data("gallery", (key, items, ids, wrap = false) => ({
    active: null,
    touch: null,
    // One item alone: its viewer has no arrows (owner, 2026-10-29)
    alone: items.length < 2,
    // The item `by` places from `from`: none past either end (wrapping, round to the other), for by 0, or alone
    galleryNext(from, by) {
        const at = items.indexOf(from) + by;
        const to = wrap ? items[(at + items.length) % items.length] : items[at];
        return by && to !== from ? to ?? null : null;
    },
    // The large view moves `by` places; the open viewer moves there (the large view too)
    galleryShow(by) {
        this.active = this.galleryNext(this.active, by) ?? this.active;
    },
    galleryStep(from, by) {
        const to = this.galleryNext(from, by);
        if (!to) return;
        this.$dispatch(`dialog-media-${ids[from]}`, "close");
        this.active = to;
        this.$dispatch(`dialog-media-${ids[to]}`, "open");
    },
    // A swipe, on the large view or in the viewer: swipeStart on touchstart, then swipe() on touchend gives 1 (to the
    // next, a swipe left), -1 (back) or 0 (a tap, or mostly up or down)
    swipeStart(event) {
        this.touch = [event.touches[0].clientX, event.touches[0].clientY];
    },
    swipe(event) {
        if (!this.touch) return 0;
        const dx = event.changedTouches[0].clientX - this.touch[0], dy = event.changedTouches[0].clientY - this.touch[1];
        this.touch = null;
        return Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) ? (dx < 0 ? 1 : -1) : 0;
    },

    init() {
        const before = galleries[key];
        const added = before ? items.find((item) => !before.items.includes(item)) : undefined;
        const at = before ? before.items.indexOf(before.active) : -1;
        const nearest = at < 0 ? [] : [...before.items.slice(0, at).reverse(), ...before.items.slice(at + 1)];

        this.active = added
            ?? (before && items.includes(before.active) ? before.active : null)
            ?? nearest.find((item) => items.includes(item)) ?? items[0] ?? null;

        galleries[key] = {active: this.active, items};
        this.$watch("active", (active) => galleries[key] = {active, items});
    },
}));

/*
 * Swiped away (owner, 2026-10-24: notifications): a finger drags the element sideways; let go past a third of its width
 * and it slides off, folds shut and `url` is deleted (DELETE), else it springs back. Up and down still scroll the page
 * (touch-action: pan-y). dismiss() does the same from a button (the × beside it, for a mouse or keyboard). If the server
 * refuses, it comes back and says so. The answer's unread count goes to the Alerts badge ([data-badge], its only badge).
 */
Alpine.data("swipeAway", (url) => ({
    start: null,
    dx: 0,
    sideways: false,

    init() {
        this.$root.style.touchAction = "pan-y";
    },

    begin(event) {
        this.start = [event.touches[0].clientX, event.touches[0].clientY];
        this.sideways = false;
        this.$root.style.transition = "none";
    },

    drag(event) {
        if (!this.start) return;
        const dx = event.touches[0].clientX - this.start[0], dy = event.touches[0].clientY - this.start[1];
        // Decided once, on the first clear move: sideways drags it, up or down leaves it to the page
        if (!this.sideways && Math.abs(dx) > 10 && Math.abs(dx) > Math.abs(dy)) this.sideways = true;
        if (!this.sideways) {
            if (Math.abs(dy) > 10) this.start = null;
            return;
        }
        this.dx = dx;
        this.$root.style.transform = `translateX(${dx}px)`;
        this.$root.style.opacity = String(1 - Math.min(Math.abs(dx) / this.$root.offsetWidth, 1) * 0.7);
    },

    end() {
        if (!this.start) return;
        this.start = null;
        if (!this.sideways) return;
        Math.abs(this.dx) > this.$root.offsetWidth / 3 ? this.dismiss(Math.sign(this.dx)) : this.back();
    },

    // The tap that ends a sideways drag doesn't also open what was dragged
    click(event) {
        if (this.sideways) {
            event.preventDefault();
            event.stopPropagation();
            this.sideways = false;
        }
    },

    back() {
        this.$root.style.transition = "transform 150ms ease-out, opacity 150ms ease-out";
        this.$root.style.transform = "";
        this.$root.style.opacity = "";
        this.dx = 0;
    },

    // The card (x-data's element), also when its × calls this: $el would be the button
    async dismiss(direction = 1) {
        const el = this.$root;
        el.style.transition = "transform 150ms ease-in, opacity 150ms ease-in";
        el.style.transform = `translateX(${direction * el.offsetWidth}px)`;
        el.style.opacity = "0";

        const response = await fetch(url, {
            method: "DELETE",
            headers: {"Accept": "application/json", "X-CSRF-TOKEN": document.querySelector('meta[name="csrf-token"]')?.content ?? ""},
        }).catch(() => null);

        if (!response?.ok) {
            this.back();
            window.Toaster?.error("That couldn't be removed. Try again.");
            return;
        }

        const {unread} = await response.json();
        document.querySelectorAll("[data-badge]").forEach((badge) => unread
            ? badge.firstChild.textContent = unread > 99 ? "99+" : String(unread)
            : badge.remove());

        // Folds shut, so what's under it moves up, then it's gone
        el.style.height = `${el.offsetHeight}px`;
        el.style.overflow = "hidden";
        requestAnimationFrame(() => {
            el.style.transition = "height 150ms ease-in, margin 150ms ease-in, padding 150ms ease-in";
            Object.assign(el.style, {height: "0px", marginTop: "0px", marginBottom: "0px", paddingTop: "0px", paddingBottom: "0px"});
        });
        setTimeout(() => {
            const list = el.parentElement;
            el.remove();
            list?.dispatchEvent(new CustomEvent("swiped-away", {bubbles: true}));
        }, 170);
    },
}));

Alpine.data("toggleable", (lockScroll = false) => openable({
    opened() {
        if (lockScroll) layers.lock(true);
    },
    closed() {
        if (lockScroll) layers.lock(false);
    },
}));

// Native <dialog>: showModal() gives the backdrop, focus trap and top layer for free. leave: see openable
Alpine.data("dialog", (leave = null) => Object.assign(openable({
    opened() {
        this.$refs.dialog.showModal();
    },
    closed() {
        if (this.$refs.dialog.open) this.$refs.dialog.close();
    },
}, true), {leave}));

/*
 * A hero's looping background video (components/hero). Nothing downloads for visitors with
 * reduced motion or data saving: they keep the still image. Otherwise it loads, fades in once it
 * is really playing (no black flash), and pauses while off screen or in a hidden tab.
 */
Alpine.data("heroVideo", () => ({
    init() {
        const video = this.$el;

        if (matchMedia('(prefers-reduced-motion: reduce)').matches || navigator.connection?.saveData) return;

        video.querySelectorAll('source[data-src]').forEach((source) => (source.src = source.dataset.src));
        video.load();
        video.addEventListener('playing', () => video.classList.remove('opacity-0'), {once: true});

        this.play = () => (document.hidden || !this.visible ? video.pause() : video.play().catch(() => {}));
        this.observer = new IntersectionObserver(([entry]) => {
            this.visible = entry.isIntersecting;
            this.play();
        });
        this.observer.observe(video);
        document.addEventListener('visibilitychange', this.play);
    },

    destroy() {
        this.observer?.disconnect();
        document.removeEventListener('visibilitychange', this.play);
    },
}));

/*
 * Tabs (components/tab/*): one container, a tablist of buttons, and panels, following the WAI-ARIA
 * tabs pattern. The URL hash opens a tab, but only one this container has a panel for, so nested
 * tab sets don't steal each other's names. Arrow keys, Home and End move between tabs.
 *   tab:  the tab open at first
 *   hash: follow the URL hash (on load, and on back/forward)
 */
Alpine.data("tab", (tab = null, hash = true) => ({
    tab: tab,
    hash: hash,

    init() {
        if (hash) {
            this.fromHash();
            window.addEventListener('hashchange', () => this.fromHash());
        }
    },

    /**
     * Opens a tab. With an href ('#name') the whole stack of open tabs goes in the URL, outermost first
     * (#communication/sms/sendmode-settings), so a refresh or a shared link reopens every level.
     */
    change(tab, href = null) {
        this.tab = tab;

        if (typeof href === 'string' && href.startsWith('#')) {
            const url = '#' + this.path().map(encodeURIComponent).join('/');
            if (location.hash !== url) {
                history.replaceState(history.state, '', url);
            }
        }
    },

    /**
     * Opens the tab the URL names at this level: one of its segments, or the tab holding a nested one it
     * names (an older single-name link like #sendmode-settings still opens every level above it).
     */
    fromHash() {
        const names = decodeURIComponent(location.hash.slice(1)).split('/').filter(Boolean);
        const panels = this.panels();
        const panel = panels.find((panel) => names.includes(panel.dataset.tab))
            ?? panels.find((panel) => names.some((name) => panel.querySelector(`[role="tabpanel"][data-tab="${CSS.escape(name)}"]`)));

        if (panel) {
            this.tab = panel.dataset.tab;
        }
    },

    /** The open tab at every level that keeps the URL: the tab sets around this one, this one, and the ones inside it. */
    path() {
        const up = [];
        for (let set = this.$root.parentElement?.closest('[data-tabs]'); set; set = set.parentElement?.closest('[data-tabs]')) {
            if (Alpine.$data(set).hash) {
                up.unshift(Alpine.$data(set).tab);
            }
        }

        const down = [];
        for (let set = this.$root; set;) {
            const data = Alpine.$data(set);
            if (set !== this.$root && data.hash) {
                down.push(data.tab);
            }
            const open = data.panels().find((panel) => panel.dataset.tab === data.tab);
            set = open && [...open.querySelectorAll('[data-tabs]')].find((inner) => inner.parentElement.closest('[data-tabs]') === set);
        }

        return [...up, this.tab, ...down];
    },

    /** This container's own panels and buttons, not those of a tab set nested inside it. */
    panels() {
        return this.owned('[role="tabpanel"]');
    },

    buttons() {
        return this.owned('[role="tab"]').filter((button) => button.offsetParent !== null);
    },

    owned(selector) {
        return [...this.$root.querySelectorAll(selector)].filter((el) => el.closest('[data-tabs]') === this.$root);
    },

    /** Arrow keys, Home and End move to (and open) the previous, next, first or last tab. */
    keys(event) {
        const buttons = this.buttons();
        const index = buttons.indexOf(document.activeElement);
        const target = {
            ArrowRight: buttons[(index + 1) % buttons.length],
            ArrowDown: buttons[(index + 1) % buttons.length],
            ArrowLeft: buttons[(index - 1 + buttons.length) % buttons.length],
            ArrowUp: buttons[(index - 1 + buttons.length) % buttons.length],
            Home: buttons[0],
            End: buttons[buttons.length - 1],
        }[event.key];

        if (index !== -1 && target) {
            event.preventDefault();
            target.focus();

            // Buttons that act (a Google sign-in) rather than show a panel only take focus
            if (this.panels().some((panel) => panel.dataset.tab === target.dataset.tab)) {
                this.change(target.dataset.tab, target.dataset.href || null);
            }
        }
    },
}));

Alpine.data("preview", () => ({
    async handlePreview(response) {
        if (response.error) {
            alert(response.error);
            return;
        }

        const blob = new Blob([Uint8Array.from(atob(response.data), c => c.charCodeAt(0))], {
            type: response.mimeType
        });
        const url = URL.createObjectURL(blob);

        window.open(url, '_blank');
        setTimeout(() => URL.revokeObjectURL(url), 1000);
    }
}));
